import { useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "api";

const POLL_INTERVAL_MS = 2500;
export const SPD_INVOICE_WAIT_MS = 60000;

export const useInvoicingStatus = () =>
  useQuery({
    queryKey: ["spd-status"],
    queryFn: api.agronomistInvoicing.fetchStatus,
    staleTime: 0,
  });

export const useEntersoftAccount = () => {
  const queryClient = useQueryClient();
  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ["spd-status"] });
    queryClient.invalidateQueries({ queryKey: ["spd-entersoft-account"] });
  };

  const account = useQuery({
    queryKey: ["spd-entersoft-account"],
    queryFn: api.agronomistInvoicing.fetchEntersoftAccount,
  });
  const connect = useMutation({
    mutationKey: ["spd-entersoft-connect"],
    mutationFn: api.agronomistInvoicing.connectEntersoft,
    onSuccess: refresh,
  });
  const disconnect = useMutation({
    mutationKey: ["spd-entersoft-disconnect"],
    mutationFn: api.agronomistInvoicing.disconnectEntersoft,
    onSuccess: refresh,
  });

  return { account, connect, disconnect };
};

export const useSpdFarmerCheck = ({ taxNumber, unitPrice, year }, enabled) =>
  useQuery({
    queryKey: ["spd-farmer-check", taxNumber, unitPrice, year],
    queryFn: () =>
      api.agronomistInvoicing.checkFarmer({ taxNumber, unitPrice, year }),
    enabled,
    retry: false,
    staleTime: 60000,
  });

export const useCreateSpdInvoice = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["spd-invoice-create"],
    mutationFn: api.agronomistInvoicing.createInvoice,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["spd-invoices"] });
      queryClient.invalidateQueries({ queryKey: ["spd-farmer-check"] });
      queryClient.invalidateQueries({ queryKey: ["my-farmers"] });
    },
  });
};

export const useIssueSpdDrafts = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["spd-invoice-issue-drafts"],
    mutationFn: api.agronomistInvoicing.issueDrafts,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["spd-invoices"] });
      queryClient.invalidateQueries({ queryKey: ["my-farmers"] });
    },
  });
};

export const useCreateSpdInvoiceBatch = () =>
  useMutation({
    mutationKey: ["spd-invoice-batch-create"],
    mutationFn: api.agronomistInvoicing.createInvoiceBatch,
  });

// Polls a batch until it is done or paused; the batch is the source of truth,
// the mutation responses only seed it.
export const useSpdInvoiceBatch = (batchId) => {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: ["spd-invoice-batch", batchId],
    queryFn: async () => {
      const batch = await api.agronomistInvoicing.fetchInvoiceBatch(batchId);
      if (["done", "paused"].includes(batch.status)) {
        queryClient.invalidateQueries({ queryKey: ["spd-invoices"] });
        queryClient.invalidateQueries({ queryKey: ["my-farmers"] });
      }
      return batch;
    },
    enabled: Boolean(batchId),
    gcTime: 0,
    refetchInterval: (q) =>
      ["done", "paused"].includes(q.state.data?.status) ? false : POLL_INTERVAL_MS,
  });
};

export const useResumeSpdInvoiceBatch = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["spd-invoice-batch-resume"],
    mutationFn: api.agronomistInvoicing.resumeInvoiceBatch,
    onSuccess: (batch) =>
      queryClient.setQueryData(["spd-invoice-batch", batch.id], batch),
  });
};

export const useUpdateSpdInvoice = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["spd-invoice-update"],
    mutationFn: api.agronomistInvoicing.updateInvoice,
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["spd-invoices"] }),
  });
};

export const useDeleteSpdInvoice = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["spd-invoice-delete"],
    mutationFn: api.agronomistInvoicing.deleteInvoice,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["spd-invoices"] });
      queryClient.invalidateQueries({ queryKey: ["my-farmers"] });
    },
  });
};

export const useRetrySpdInvoice = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["spd-invoice-retry"],
    mutationFn: api.agronomistInvoicing.retryInvoice,
    onSuccess: (invoice) => {
      queryClient.setQueryData(["spd-invoice", invoice.id], invoice);
      queryClient.invalidateQueries({ queryKey: ["spd-invoices"] });
    },
  });
};

// Polls an invoice while it is being issued (action "wait"), for at most
// SPD_INVOICE_WAIT_MS. A new id or a new retry attempt restarts the clock.
export const useSpdInvoicePolling = (invoiceId, attempt = 0) => {
  const queryClient = useQueryClient();
  const startedAt = useRef({ key: null, at: 0 });
  const key = `${invoiceId}-${attempt}`;
  if (invoiceId && startedAt.current.key !== key) {
    startedAt.current = { key, at: Date.now() };
  }
  const isWithinWait = () =>
    Date.now() - startedAt.current.at < SPD_INVOICE_WAIT_MS;

  const query = useQuery({
    queryKey: ["spd-invoice", invoiceId],
    queryFn: async () => {
      const invoice = await api.agronomistInvoicing.fetchInvoice(invoiceId);
      if (invoice.action !== "wait") {
        queryClient.invalidateQueries({ queryKey: ["spd-invoices"] });
        queryClient.invalidateQueries({ queryKey: ["my-farmers"] });
      }
      return invoice;
    },
    enabled: Boolean(invoiceId),
    gcTime: 0,
    refetchInterval: (q) =>
      q.state.data?.action === "wait" && isWithinWait() ? POLL_INTERVAL_MS : false,
  });

  return {
    invoice: query.data,
    isTimedOut: query.data?.action === "wait" && !isWithinWait(),
  };
};

export const useSpdInvoices = (filters) =>
  useQuery({
    queryKey: ["spd-invoices", filters],
    queryFn: () => api.agronomistInvoicing.fetchInvoices(filters),
  });
